import { useMemo } from 'react';
import { useQuery, useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { jurisprudentieService, type SearchFilters, type JurisprudentieWithHighlight } from '@shared/services/jurisprudentie.service';
import { queryKeys } from '@shared/lib/react-query';
import type { Database, PortalType } from '@shared/types/database';
import { groupByDay, getDaysArray, isSameDay, createEmptyDayGroup, type DayGroup } from '@shared/lib/dateUtils';
import { startOfDay, subDays } from 'date-fns';

type Jurisprudentie = Database['public']['Tables']['jurisprudentie']['Row'];
type Instantie = Database['public']['Tables']['instanties']['Row'];

export interface JurisprudentieWithRelations extends Omit<Jurisprudentie, 'content_id'> {
  content_id?: string | null;
  instanties?: Instantie | null;
  jurisprudentie_rechtsgebieden?: Array<{
    rechtsgebieden?: Database['public']['Tables']['rechtsgebieden']['Row'];
  }>;
  jurisprudentie_zaak_types?: Array<{
    zaak_types?: Database['public']['Tables']['zaak_types']['Row'];
  }>;
  jurisprudentie_themas?: Array<{
    themas?: Database['public']['Tables']['themas']['Row'];
  }>;
}

// Hook for fetching recent cases with pagination
export function useRecentCases(page: number = 1, itemsPerPage: number = 10, portalIds?: PortalType[]) {
  return useQuery({
    queryKey: queryKeys.recent(page, portalIds),
    queryFn: async () => {
      const result = await jurisprudentieService.search({
        sortBy: 'date_desc',
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        portalIds // Filter by portal(s)
      });

      if (result.error) {
        throw new Error(result.error);
      }

      return {
        cases: result.data as JurisprudentieWithRelations[],
        totalCount: result.count,
        hasMore: result.count > page * itemsPerPage
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 1 * 60 * 1000, // Consider data stale after 1 minute
  });
}

// Hook for searching cases with filters
export function useSearchCases(
  searchQuery: string,
  filters: {
    selectedDomains: string[];
    selectedCourts: string[];
    selectedThemas?: string[];
    selectedWetsartikelen?: string[];
    dateRange: string;
    customDateRange?: { from: Date | undefined; to: Date | undefined };
    sortBy: string;
    caseType?: string;
  },
  page: number = 1,
  itemsPerPage: number = 10,
  enabled: boolean = false,
  portalIds?: PortalType[]  // Multi-portal filtering support (type-safe)
) {
  // Memoize date range calculations to prevent infinite re-renders
  // Date objects are compared by reference in React Query keys, so we need stable references
  const { dateFrom, dateTo } = useMemo(() => {
    if (filters.dateRange === 'all') {
      return { dateFrom: undefined, dateTo: undefined };
    }

    const now = new Date();
    let startDate: Date | undefined;
    let endDate: Date | undefined;

    switch (filters.dateRange) {
      case 'week':
        startDate = new Date();
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate = new Date();
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate = new Date();
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate = new Date();
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'custom':
        if (filters.customDateRange) {
          startDate = filters.customDateRange.from;
          endDate = filters.customDateRange.to;
        }
        break;
    }

    return { dateFrom: startDate, dateTo: endDate };
  }, [filters.dateRange, filters.customDateRange]);

  const searchParams: SearchFilters = {
    limit: itemsPerPage,
    offset: (page - 1) * itemsPerPage
  };

  // Add search query if present
  if (searchQuery.trim()) {
    searchParams.query = searchQuery.trim();
  }

  // Add sorting
  if (filters.sortBy === 'date-desc') {
    searchParams.sortBy = 'date_desc';
  } else if (filters.sortBy === 'date-asc') {
    searchParams.sortBy = 'date_asc';
  } else if (filters.sortBy === 'relevance' && searchQuery) {
    searchParams.sortBy = 'relevance';
  }

  // Add date range filter (now using memoized dates)
  if (dateFrom) {
    searchParams.dateFrom = dateFrom;
  }
  if (dateTo) {
    searchParams.dateTo = dateTo;
  }

  // Add court filters
  if (filters.selectedCourts.length > 0) {
    searchParams.instanties = filters.selectedCourts;
  }

  // Add domain filters
  if (filters.selectedDomains.length > 0) {
    searchParams.rechtsgebieden = filters.selectedDomains;
  }

  // Add thema filters
  if (filters.selectedThemas && filters.selectedThemas.length > 0) {
    searchParams.themas = filters.selectedThemas;
  }

  // Add wetsartikelen filters
  if (filters.selectedWetsartikelen && filters.selectedWetsartikelen.length > 0) {
    searchParams.wetsartikelen = filters.selectedWetsartikelen;
  }

  // Add case type filter
  if (filters.caseType && filters.caseType !== 'all') {
    searchParams.zaakTypes = [filters.caseType];
  }

  return useQuery({
    queryKey: queryKeys.search({ ...searchParams, page, portalIds }),
    queryFn: async () => {
      // Use the optimized search service that includes highlighting
      const searchFilters: SearchFilters = {
        ...searchParams,
        enableHighlighting: true, // Enable search highlighting
        portalIds  // Multi-portal filtering support
      };

      const result = await jurisprudentieService.searchOptimized(searchFilters);

      if (result.error) {
        throw new Error(result.error);
      }

      return {
        results: result.data as JurisprudentieWithHighlight[],
        totalCount: result.count,
        totalPages: Math.ceil(result.count / itemsPerPage)
      };
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000, // Consider search results stale after 5 minutes
  });
}

// Hook for fetching a single case by ID
export function useCaseDetail(id: string) {
  return useQuery({
    queryKey: queryKeys.detail(id),
    queryFn: () => jurisprudentieService.getById(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // Consider detail data stale after 10 minutes
  });
}

// Hook for infinite scroll (alternative to pagination)
export function useInfiniteRecentCases(itemsPerPage: number = 10, portalIds?: PortalType[]) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.lists(), 'infinite', portalIds],
    queryFn: async ({ pageParam = 0 }) => {
      const result = await jurisprudentieService.search({
        sortBy: 'date_desc',
        limit: itemsPerPage,
        offset: pageParam,
        portalIds // Filter by portal(s)
      });

      if (result.error) {
        throw new Error(result.error);
      }

      return {
        cases: result.data as JurisprudentieWithRelations[],
        nextOffset: pageParam + itemsPerPage,
        hasNextPage: result.count > pageParam + itemsPerPage,
        totalCount: result.count
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? lastPage.nextOffset : undefined,
  });
}

// Hook for fetching recent cases grouped by day (last 5 weekdays)
export function useRecentCasesByDay(portalIds: PortalType[]) {
  return useQuery({
    queryKey: ['jurisprudentie', 'recent-by-day', portalIds],
    queryFn: async () => {
      // Calculate the date range to cover last 5 weekdays
      // We need to look back further to account for weekends
      const today = startOfDay(new Date());
      // Look back up to 10 days to ensure we cover 5 weekdays (accounting for long weekends/holidays)
      const tenDaysAgo = subDays(today, 10);

      // Fetch all cases from the last 10 days based on publication date
      const result = await jurisprudentieService.search({
        dateFrom: tenDaysAgo,
        dateTo: new Date(), // Include today
        dateField: 'publicatie_datum', // Use publication date instead of verdict date
        sortBy: 'publication_desc', // Sort by publication date
        limit: 500, // Fetch up to 500 cases
        offset: 0,
        portalIds // Filter by current portal(s)
      });

      if (result.error) {
        throw new Error(result.error);
      }

      const cases = result.data as JurisprudentieWithRelations[];

      // Group cases by publication date (when they became available)
      const groupedByDay = groupByDay(cases, (caseItem) => caseItem.publicatie_datum);

      // Get the last 5 weekdays only
      const allDays = getDaysArray(5);

      // Create day groups, including empty days
      const dayGroups: DayGroup<JurisprudentieWithRelations>[] = allDays.map(date => {
        const existingGroup = groupedByDay.find(group =>
          isSameDay(group.date, date)
        );

        if (existingGroup) {
          return existingGroup;
        } else {
          // Create empty day group with proper labels
          return createEmptyDayGroup<JurisprudentieWithRelations>(date);
        }
      });

      // Calculate weekly statistics based on cases actually shown in day groups
      const weeklyTotal = dayGroups.reduce((sum, group) => sum + group.count, 0);

      return {
        dayGroups,
        weeklyTotal,
        totalCount: result.count
      };
    },
    staleTime: 1 * 60 * 1000, // Consider data stale after 1 minute
  });
}